import * as React from 'react';
import { useEffect, useState } from 'react';
import { Breadcrumbs, CircularProgress, Link } from "@mui/joy";
import Box from "@mui/joy/Box";
import Typography from "@mui/joy/Typography";
import HomeRoundedIcon from '@mui/icons-material/HomeRounded';
import ChevronRightRoundedIcon from '@mui/icons-material/ChevronRightRounded';
import { ToastContainer, toast } from "react-toastify";
import 'react-toastify/dist/ReactToastify.css';
import Stripe from "../dashboard-components/Stripe";
import { ConnectComponentsProvider, ConnectPayments } from "@stripe/react-connect-js";
import { loadConnectAndInitialize, StripeConnectInstance } from "@stripe/connect-js";
import GeneralApiService from "../../axios/Axios";
import { getStripePublishableKey } from "../../stripeConfig";

interface Franchise {
    location: string;
    stripe_account_linked: boolean;
}

export default function Invoices() {
    const [franchise, setFranchise] = useState<Franchise | null>(null);
    const [loadingData, setLoadingData] = useState(true);
    const [stripeConnectInstance, setStripeConnectInstance] = useState<StripeConnectInstance | null>(null);

    useEffect(() => {
        setLoadingData(true);
        const locationName = localStorage.getItem('location');

        if (!locationName || locationName === 'all') {
            toast.warn("Please select a specific franchise to view invoices.");
            setLoadingData(false);
            return;
        }

        const initializePage = async () => {
            try {
                const adsData = await GeneralApiService.getToggledAds();
                setFranchise(adsData);

                if (adsData.stripe_account_linked) {
                    const fetchClientSecret = async () => {
                        const tokenData = await GeneralApiService.getStripeInvoices();
                        return tokenData?.token?.client_secret;
                    };

                    const connectInstance = await loadConnectAndInitialize({
                        publishableKey: getStripePublishableKey(),
                        fetchClientSecret: fetchClientSecret,
                    });
                    setStripeConnectInstance(connectInstance);
                }
            } catch (error) {
                console.error("Failed to initialize invoice page:", error);
                toast.error("Could not load invoice information.");
            } finally {
                setLoadingData(false);
            }
        };

        initializePage();
    }, []);

    return (
        <>
            <ToastContainer />
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Breadcrumbs
                    size="sm"
                    aria-label="breadcrumbs"
                    separator={<ChevronRightRoundedIcon />}
                    sx={{ pl: 0 }}
                >
                    <Link underline="none" color="neutral" href="/dashboard" aria-label="Home"><HomeRoundedIcon /></Link>
                    <Link underline="hover" color="neutral" href="/dashboard" fontSize={12} fontWeight={500}>Dashboard</Link>
                    <Typography color="primary" fontWeight={500} fontSize={12}>Invoices</Typography>
                </Breadcrumbs>
            </Box>

            <Box sx={{ display: 'flex', my: 1, gap: 1, flexDirection: 'row', alignItems: 'center', flexWrap: 'wrap', justifyContent: 'space-between' }}>
                <Typography level="h2" component="h1">Invoices</Typography>
            </Box>

            {loadingData ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                    <CircularProgress size="lg" />
                </Box>
            ) : (
                <>
                    {franchise && !franchise.stripe_account_linked && <Stripe franchise={franchise} />}
                    {franchise && franchise.stripe_account_linked && stripeConnectInstance && (
                        <ConnectComponentsProvider connectInstance={stripeConnectInstance}>
                            <ConnectPayments />
                        </ConnectComponentsProvider>
                    )}
                </>
            )}
        </>
    );
}